import { apiDelete, apiGet, apiPatch, apiPost } from './api';

const API_V1 = 'https://dev.oliveeb.com/olreadia-service/api/v1';
const ACTOR_ID_HEADER_KEY = 'O-ACTOR-ID';

const withPagination = (params: Record<string, unknown> = {}) => {
  const search = new URLSearchParams();
  const merged = { page: 0, size: 20, ...params };
  Object.entries(merged).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value) !== '') {
      search.set(key, String(value));
    }
  });
  return search.toString();
};

const withActor = (actorId?: string | null) =>
  actorId ? { headers: { [ACTOR_ID_HEADER_KEY]: actorId } } : undefined;

const normalizeBusinessEntityPayload = (data: any) => {
  if (!data || typeof data !== 'object') return data;

  const businessLocationTypeId =
    data.businessLocationTypeId ||
    data.businessLocationType?.id ||
    data.businessLocationType ||
    null;
  const businessLocationTypeCode = data.businessLocationType?.code || null;

  return {
    ...data,
    ...(businessLocationTypeId ? { businessLocationTypeId } : {}),
    ...(businessLocationTypeCode ? { businessLocationTypeCode } : {}),
  };
};

const normalizeBusinessLocationPayload = (data: any) => {
  if (!data || typeof data !== 'object') return data;

  const businessLocationTypeId =
    data.businessLocationTypeId ||
    data.businessLocationType?.id ||
    data.businessLocationType ||
    null;
  const businessLocationTypeCode = data.businessLocationType?.code || null;

  return {
    ...data,
    ...(businessLocationTypeId ? { businessLocationTypeId } : {}),
    ...(businessLocationTypeCode ? { businessLocationTypeCode } : {}),
  };
};

export interface BusinessLocationType {
  id: string;
  code: string;
  label: string;
}

export interface PaginatedResponse<T> {
  content?: T[];
  totalElements?: number;
  totalPages?: number;
}

export const olreadiaService = {
  businessEntities: {
    list: <T = any>(params = {}) =>
      apiGet<PaginatedResponse<T>>(`${API_V1}/business-entities?${withPagination(params)}`),

    get: <T = any>(businessEntityId: string) =>
      apiGet<T>(`${API_V1}/business-entities/${businessEntityId}`),

    create: <T = any>(data: any, actorId?: string | null) =>
      apiPost<T>(`${API_V1}/business-entities`, normalizeBusinessEntityPayload(data), withActor(actorId)),

    update: <T = any>(businessEntityId: string, data: any, actorId?: string | null) =>
      apiPatch<T>(`${API_V1}/business-entities/${businessEntityId}`, normalizeBusinessEntityPayload(data), withActor(actorId)),

    delete: <T = any>(businessEntityId: string, actorId?: string | null) =>
      apiDelete<T>(`${API_V1}/business-entities/${businessEntityId}`, withActor(actorId)),

    locations: {
      list: <T = any>(businessEntityId: string, params = {}) =>
        apiGet<PaginatedResponse<T>>(`${API_V1}/business-entities/${businessEntityId}/business-locations?${withPagination(params)}`),

      get: <T = any>(businessEntityId: string, locationId: string) =>
        apiGet<T>(`${API_V1}/business-entities/${businessEntityId}/business-locations/${locationId}`),

      create: <T = any>(businessEntityId: string, data: any, actorId?: string | null) =>
        apiPost<T>(
          `${API_V1}/business-entities/${businessEntityId}/business-locations`,
          normalizeBusinessLocationPayload(data),
          withActor(actorId)
        ),

      update: <T = any>(businessEntityId: string, locationId: string, data: any, actorId?: string | null) =>
        apiPatch<T>(
          `${API_V1}/business-entities/${businessEntityId}/business-locations/${locationId}`,
          normalizeBusinessLocationPayload(data),
          withActor(actorId)
        ),

      delete: <T = any>(businessEntityId: string, locationId: string, actorId?: string | null) =>
        apiDelete<T>(
          `${API_V1}/business-entities/${businessEntityId}/business-locations/${locationId}`,
          withActor(actorId)
        ),
    },

    users: {
      list: <T = any>(businessEntityId: string, params = {}) =>
        apiGet<PaginatedResponse<T>>(`${API_V1}/business-entities/${businessEntityId}/users?${withPagination(params)}`),

      add: <T = any>(businessEntityId: string, data: { email: string; role: string; status?: string; primaryContact?: boolean }, actorId?: string | null) =>
        apiPost<T>(
          `${API_V1}/business-entities/${businessEntityId}/users`,
          data,
          withActor(actorId)
        ),

      update: <T = any>(businessEntityId: string, userId: string, data: any, actorId?: string | null) =>
        apiPatch<T>(
          `${API_V1}/business-entities/${businessEntityId}/users/${userId}`,
          data,
          withActor(actorId)
        ),

      remove: <T = any>(businessEntityId: string, userId: string, actorId?: string | null) =>
        apiDelete<T>(
          `${API_V1}/business-entities/${businessEntityId}/users/${userId}`,
          withActor(actorId)
        ),
    },
  },

  businessLocationTypes: {
    list: (params = {}) =>
      apiGet<PaginatedResponse<BusinessLocationType>>(`${API_V1}/business-location-types?${withPagination({ size: 50, ...params })}`),
  },

  serviceOfferings: {
    list: <T = any>(params = {}) =>
      apiGet<PaginatedResponse<T>>(`${API_V1}/service-offerings?${withPagination(params)}`),

    get: <T = any>(serviceId: string) =>
      apiGet<T>(`${API_V1}/service-offerings/${serviceId}`),

    create: <T = any>(data: any, actorId?: string | null) =>
      apiPost<T>(`${API_V1}/service-offerings`, data, withActor(actorId)),

    update: <T = any>(serviceId: string, data: any, actorId?: string | null) =>
      apiPatch<T>(`${API_V1}/service-offerings/${serviceId}`, data, withActor(actorId)),

    delete: <T = any>(serviceId: string, actorId?: string | null) =>
      apiDelete<T>(`${API_V1}/service-offerings/${serviceId}`, withActor(actorId)),

    businessEntityServices: {
      list: <T = any>(businessEntityId: string) =>
        apiGet<PaginatedResponse<T>>(`${API_V1}/service-offerings/business-entities/${businessEntityId}`),

      // FIX: POST body is null (this is correct — it's a link endpoint, no body needed)
      add: <T = any>(businessEntityId: string, serviceId: string, actorId?: string | null) =>
        apiPost<T>(
          `${API_V1}/service-offerings/business-entities/${businessEntityId}/business-offerings/${serviceId}`,
          null,
          withActor(actorId)
        ),

      remove: <T = any>(businessEntityId: string, serviceId: string, actorId?: string | null) =>
        apiDelete<T>(
          `${API_V1}/service-offerings/business-entities/${businessEntityId}/business-offerings/${serviceId}`,
          withActor(actorId)
        ),
    },

    businessLocationServices: {
      list: <T = any>(businessEntityId: string, businessLocationId: string) =>
        apiGet<PaginatedResponse<T>>(
          `${API_V1}/service-offerings/business-entities/${businessEntityId}/business-locations/${businessLocationId}`
        ),

      add: <T = any>(businessEntityId: string, businessLocationId: string, serviceId: string, actorId?: string | null) =>
        apiPost<T>(
          `${API_V1}/service-offerings/business-entities/${businessEntityId}/business-locations/${businessLocationId}/business-offerings/${serviceId}`,
          null,
          withActor(actorId)
        ),

      remove: <T = any>(businessEntityId: string, businessLocationId: string, serviceId: string, actorId?: string | null) =>
        apiDelete<T>(
          `${API_V1}/service-offerings/business-entities/${businessEntityId}/business-locations/${businessLocationId}/business-offerings/${serviceId}`,
          withActor(actorId)
        ),
    },
  },
};

export default olreadiaService;
